// src/utils/complianceFormatters.js
// Pure formatting/status helpers for Compliance.jsx.
// No API calls, workflow state changes, or side effects.

export function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function formatTimestamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export function formatDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

export function includesNormalized(haystack, needle) {
  const h = normalizeText(haystack);
  const n = normalizeText(needle);
  if (!n) return false;
  return h.includes(n);
}

export function cleanValue(value, fallback = "—") {
  const v = String(value ?? "").trim();
  return v || fallback;
}

export function normalizeRouteParamValue(value) {
  const v = String(value ?? "").trim();
  const lowered = v.toLowerCase();

  if (!v || lowered === "undefined" || lowered === "null" || lowered === "—") {
    return "";
  }

  return v;
}

export function buildControlLabel(controlId, ruleId) {
  const left = String(controlId || "").trim();
  const right = String(ruleId || "").trim();

  if (!left && !right) return "Unnamed control";
  if (left && right && left !== right) return `${left} · ${right}`;
  return left || right;
}

export function isClosedTask(task) {
  const status = normalizeText(task?.status);
  return status === "closed" || status === "cancelled" || status === "canceled";
}

export function isTaskOverdue(task) {
  if (!task?.due_date || isClosedTask(task)) return false;

  const due = new Date(`${task.due_date}T23:59:59`);
  if (Number.isNaN(due.getTime())) return false;

  return due.getTime() < Date.now();
}

export function getTaskBlockReason(task) {
  return (
    task?.task_metadata?.block_reason ||
    task?.task_metadata?.blocked_reason ||
    task?.task_metadata?.blockReason ||
    ""
  );
}

export function formatRelativeTime(value) {
  if (!value) return "—";

  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return "—";

  const diffMs = Date.now() - ts;
  const diffMin = Math.round(diffMs / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

export function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

export function getTaskStatusLabel(status) {
  return cleanValue(status, "open")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function normalizeBooleanish(value) {
  if (value === true) return true;
  if (value === false) return false;

  const cleaned = normalizeText(value);
  if (["true", "t", "yes", "y", "1", "active"].includes(cleaned)) return true;
  if (["false", "f", "no", "n", "0", "inactive", "n/a", "na", "none", "null"].includes(cleaned)) return false;

  return null;
}

export function getCredentialStatusLabel(value) {
  const normalized = normalizeBooleanish(value);
  if (normalized === true) return "Active";
  if (normalized === false) return "Inactive";
  return cleanValue(value, "—");
}

export function isCredentialActive(value) {
  return normalizeBooleanish(value) === true;
}

export function formatCredentialTimestamp(value) {
  const cleaned = cleanValue(value, "");
  const lowered = normalizeText(cleaned);

  if (!cleaned || cleaned === "—" || ["n/a", "na", "none", "null", "no_information"].includes(lowered)) {
    return "—";
  }

  return formatTimestamp(cleaned);
}

export function getInvestigationStatusLabel(status) {
  const cleaned = normalizeText(status || "not_started");
  if (cleaned === "not_started") return "Not Started";
  if (cleaned === "investigating") return "Investigating";
  if (cleaned === "dependency_identified") return "Dependency Identified";
  if (cleaned === "rotation_planned") return "Rotation Planned";
  if (cleaned === "replacement_testing") return "Replacement Testing";
  if (cleaned === "monitoring") return "Monitoring";
  if (cleaned === "rotation_complete") return "Rotation Complete";
  if (cleaned === "ready_for_validation") return "Ready For Validation";
  return getTaskStatusLabel(cleaned);
}

export function getEventLabel(eventType) {
  return getTaskStatusLabel(eventType || "event");
}

export function getEventTone(eventType) {
  const event = normalizeText(eventType);
  if (event.includes("failed") || event.includes("blocked")) return "danger";
  if (event.includes("passed") || event.includes("validated") || event.includes("closed")) return "ok";
  if (event.includes("ready")) return "warn";
  if (event.includes("changed") || event.includes("created")) return "accent";
  return "default";
}

export function getEventToneColor(eventType) {
  const tone = getEventTone(eventType);
  if (tone === "ok") return "var(--ok)";
  if (tone === "warn") return "var(--warn)";
  if (tone === "danger") return "var(--danger)";
  if (tone === "accent") return "var(--accent)";
  return "var(--muted)";
}

export const AUDIT_VALUE_LABELS = {
  acknowledged: "Acknowledged",
  active: "Active",
  blocked: "Blocked",
  canceled: "Canceled",
  cancelled: "Cancelled",
  closed: "Closed",
  deactivated: "Deactivated",
  deleted: "Deleted",
  failed: "Failed",
  inactive: "Inactive",
  in_progress: "In Progress",
  investigating: "Investigating",
  monitoring: "Monitoring",
  not_recorded: "Not Recorded",
  not_started: "Not Started",
  open: "Open",
  passed: "Passed",
  pending: "Pending",
  pending_approval: "Pending Approval",
  pending_rotation: "Pending Rotation",
  ready_for_validation: "Ready For Validation",
  rejected: "Rejected",
  released: "Released",
  replacement_not_required: "Replacement Not Required",
  replacement_testing: "Replacement Testing",
  resolved: "Resolved",
  rotated_in_place: "Rotated In Place",
  rotation_complete: "Rotation Complete",
  rotation_planned: "Rotation Planned",
  snoozed: "Snoozed",
  validated: "Validated",
};

export function humanizeAuditValue(value, fallback = "—") {
  const raw = cleanValue(value, "");
  if (!raw || raw === "—") return fallback;

  const key = normalizeText(raw);
  if (AUDIT_VALUE_LABELS[key]) return AUDIT_VALUE_LABELS[key];

  if (key.includes("_")) return getTaskStatusLabel(key);

  return raw;
}

export function formatAuditTransition(fromState, toState) {
  const fromLabel = humanizeAuditValue(fromState, "");
  const toLabel = humanizeAuditValue(toState, "");

  if (fromLabel && toLabel) return `${fromLabel} → ${toLabel}`;
  return "";
}

export function humanizeAuditText(value) {
  const text = cleanValue(value, "");
  if (!text) return "";

  return text.replace(
    /\bfrom\s+([A-Za-z0-9_]+)\s+to\s+([A-Za-z0-9_]+)\b/g,
    (_match, fromState, toState) =>
      `from ${humanizeAuditValue(fromState, fromState)} to ${humanizeAuditValue(toState, toState)}`
  );
}

export function getEventDetail(event) {
  const metadata = event?.event_metadata || {};
  const message = cleanValue(event?.message, "");

  if (metadata.reason) return humanizeAuditText(metadata.reason);
  if (message) return humanizeAuditText(message);

  const fromState = cleanValue(event?.from_state, "");
  const toState = cleanValue(event?.to_state, "");

  if (fromState && toState) return formatAuditTransition(fromState, toState);
  return "Task event recorded.";
}

export function getDisplayStateLabel(value, fallback = "—") {
  const cleaned = cleanValue(value, "");
  if (!cleaned || cleaned === "—") return fallback;
  return getTaskStatusLabel(cleaned);
}

export function getFixPlanDisplayState(fixPlanItem = {}, task = null) {
  const taskStatus = normalizeText(task?.status);
  const validationStatus = normalizeText(task?.validation_status);

  if (taskStatus === "closed") return "Closed";
  if (taskStatus === "validated" || validationStatus === "passed") return "Validated";
  if (taskStatus) return getTaskStatusLabel(taskStatus);

  return getDisplayStateLabel(fixPlanItem?.state, "—");
}

export function getFixPlanDisplayStateTone(task = null, fallbackState = "") {
  const taskStatus = normalizeText(task?.status);
  const validationStatus = normalizeText(task?.validation_status);
  const fallback = normalizeText(fallbackState);

  // The Fix Plan State metric represents the remediation workflow state.
  // A prior validation failure should not make "Ready For Validation" appear red;
  // red is reserved for blocked/failed workflow states and explicit failed fallbacks.
  if (taskStatus === "closed" || taskStatus === "validated" || validationStatus === "passed") return "ok";
  if (taskStatus === "ready_for_validation") return "warn";
  if (taskStatus === "in_progress") return "accent";
  if (taskStatus === "blocked" || taskStatus === "failed" || fallback.includes("fail")) return "danger";
  if (validationStatus === "failed") return "warn";
  return "default";
}

export function getTaskTone(status) {
  const cleaned = normalizeText(status);

  // Green is reserved for proven-success states.
  // Active work should read as active/blue, not complete/green.
  if (cleaned === "validated") return "ok";
  if (cleaned === "blocked" || cleaned === "failed") return "danger";
  if (cleaned === "ready_for_validation") return "warn";
  if (cleaned === "in_progress") return "active";
  return "default";
}

export function getToneColor(tone = "default") {
  if (tone === "ok") return "var(--ok)";
  if (tone === "warn") return "var(--warn)";
  if (tone === "danger") return "var(--danger)";

  // Use an explicit active blue instead of var(--accent), because OASYS' accent
  // is green and makes active/in-progress work look complete.
  if (tone === "active" || tone === "accent") return "var(--active, #60a5fa)";

  return "var(--muted)";
}

export function getSummaryChipStyle(isActive, tone = "default") {
  const base = {
    padding: "4px 9px",
    fontSize: 11,
    lineHeight: 1.1,
  };

  if (!isActive) return base;

  const color = getToneColor(tone);

  return {
    ...base,
    color,
    background: `color-mix(in srgb, ${color}, transparent 90%)`,
    border: `1px solid color-mix(in srgb, ${color}, transparent 70%)`,
  };
}
